import React, { useState, useEffect, useMemo } from 'react';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Filter, Calendar, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MONTHS } from '../constants';
import { formatPaymentName, getTotalPaymentsFromType } from '../utils/helpers';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Facturacion = () => {
  const { user } = useAuth();
  const [samples, setSamples] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user should have restricted access
  const isRestrictedUser = user && !user.is_superuser && user.assigned_destinations?.length === 1;
  const restrictedDestination = isRestrictedUser ? user.assigned_destinations[0] : null;

  const [filters, setFilters] = useState({
    billing_status: 'all',
    year: 'all',
    month: 'all',
    destination: 'all'
  });

  // Helper function to extract year from payment (hito de facturación)
  const extractYearFromPayment = (payment) => {
    // Get year from payment_year field
    if (payment && payment.payment_year) {
      return String(payment.payment_year);
    }
    return null;
  };

  // Get available years from payment milestones (hitos de facturación)
  const availableYears = useMemo(() => {
    const years = new Set();
    samples.forEach(sample => {
      if (sample.payment_config && sample.payment_config.payments) {
        sample.payment_config.payments.forEach(payment => {
          const year = extractYearFromPayment(payment);
          if (year) {
            years.add(year);
          }
        });
      }
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a)); // Most recent first
  }, [samples]);

  // Format number with thousand separator
  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    let num = price;
    if (typeof price === 'string') {
      num = parseFloat(price.replace(/\./g, '').replace(',', '.'));
    }
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    }).format(num);
  };

  useEffect(() => {
    fetchSamples();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, samples]);

  const fetchSamples = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/work-samples');
      // Filter only samples with payment config (all destinations with billing system)
      const samplesWithPayments = response.data.filter(
        s => s.payment_config && s.payment_config.payments && s.payment_config.payments.length > 0
      );
      setSamples(samplesWithPayments);
    } catch (error) {
      console.error('Error fetching samples:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...samples];

    // Create array of {sample, payment} objects for filtering
    let samplePayments = [];
    filtered.forEach(sample => {
      if (sample.payment_config && sample.payment_config.payments) {
        sample.payment_config.payments.forEach(payment => {
          // Show ALL payments (both paid and unpaid)
          samplePayments.push({ sample, payment });
        });
      }
    });

    // Filter by destination
    const destinationFilter = isRestrictedUser ? restrictedDestination : filters.destination;
    if (destinationFilter !== 'all') {
      samplePayments = samplePayments.filter(
        sp => sp.sample.destination === destinationFilter
      );
    }

    // Filter by year (using payment_year from hito de facturación)
    if (filters.year !== 'all') {
      samplePayments = samplePayments.filter(sp => {
        const year = extractYearFromPayment(sp.payment);
        return year === filters.year;
      });
    }

    // Filter by billing status
    if (filters.billing_status !== 'all') {
      samplePayments = samplePayments.filter(
        sp => sp.payment.billing_status === filters.billing_status
      );
    }

    // Filter by month
    if (filters.month !== 'all') {
      samplePayments = samplePayments.filter(
        sp => sp.payment.payment_month === filters.month
      );
    }

    // Sort alphabetically by client name
    samplePayments.sort((a, b) => {
      const clientA = a.sample.client?.toLowerCase() || '';
      const clientB = b.sample.client?.toLowerCase() || '';
      return clientA.localeCompare(clientB, 'es');
    });

    setFilteredSamples(samplePayments);
  };

  const clearFilters = () => {
    setFilters({
      billing_status: 'all',
      year: 'all',
      month: 'all',
      destination: 'all'
    });
  };

  const getTotalAmount = () => {
    return filteredSamples.reduce((sum, sp) => sum + (sp.payment.amount || 0), 0);
  };

  // Calculate Coste Goya for a single entry (proportional to hito)
  const getCosteGoya = (sp) => {
    const dest = sp.sample.destination;
    if (dest === 'ANMAR' || dest === 'ESTABILIDADES') return null;
    const coste = sp.sample.coste_goya || sp.sample.precio_compra;
    if (!coste) return null;
    // Proportional cost per hito
    const totalAmount = sp.sample.payment_config?.total_amount || sp.payment.amount;
    const ratio = totalAmount > 0 ? sp.payment.amount / totalAmount : 1;
    return Math.round(coste * ratio * 100) / 100;
  };

  // Calculate Margen Goya for a single entry
  const getMargenGoya = (sp) => {
    const dest = sp.sample.destination;
    const amount = sp.payment.amount || 0;
    if (dest === 'ANMAR' || dest === 'ESTABILIDADES') return null; // No margen for ANMAR/ESTABILIDADES
    const costeTotal = sp.sample.coste_goya || sp.sample.precio_compra || 0;
    if (dest === 'REGULATORY') {
      if (!costeTotal) return amount;
      const totalAmount = sp.sample.payment_config?.total_amount || amount;
      const ratio = totalAmount > 0 ? amount / totalAmount : 1;
      const costeProporcional = costeTotal * ratio;
      return Math.round((amount - costeProporcional) * 100) / 100;
    }
    // GAIKER / IVAMI - proportional cost per hito
    const totalAmount = sp.sample.payment_config?.total_amount || amount;
    const ratio = totalAmount > 0 ? amount / totalAmount : 1;
    const costeProporcional = costeTotal * ratio;
    return Math.round((amount - costeProporcional) * 100) / 100;
  };

  // Summary totals (only GAIKER, IVAMI, REGULATORY)
  const activeDestination = isRestrictedUser ? restrictedDestination : filters.destination;

  const getTotalCosteGoya = () => {
    return filteredSamples
      .filter(sp => sp.sample.destination !== 'ANMAR' && sp.sample.destination !== 'ESTABILIDADES')
      .reduce((sum, sp) => sum + (getCosteGoya(sp) || 0), 0);
  };

  const getTotalMargenGoya = () => {
    return filteredSamples
      .filter(sp => sp.sample.destination !== 'ANMAR' && sp.sample.destination !== 'ESTABILIDADES')
      .reduce((sum, sp) => sum + (getMargenGoya(sp) || 0), 0);
  };

  const handleBillingStatusChange = async (sampleId, paymentIndex, newStatus) => {
    try {
      // Find the sample
      const sample = samples.find(s => s.id === sampleId);
      if (!sample || !sample.payment_config) return;

      // Update the payment
      const updatedPayments = [...sample.payment_config.payments];
      updatedPayments[paymentIndex] = {
        ...updatedPayments[paymentIndex],
        billing_status: newStatus
      };

      const updatedPaymentConfig = {
        total_amount: sample.payment_config.total_amount,
        payment_type: sample.payment_config.payment_type,
        payments: updatedPayments
      };

      // Send update to backend
      await axios.put(`/work-samples/${sampleId}`, {
        payment_config: updatedPaymentConfig
      });

      toast.success('Estado de facturación actualizado');
      
      // Reload data
      await fetchSamples();
    } catch (error) {
      console.error('Error updating billing status:', error);
      toast.error('Error al actualizar el estado de facturación');
    }
  };

  const generatePDF = () => {
    if (filteredSamples.length === 0) {
      toast.error('No hay datos para generar el PDF');
      return;
    }

    // Determine which destinations are present in filtered data
    const destinationsInData = [...new Set(filteredSamples.map(sp => sp.sample.destination))];

    // Build unified columns and rows for all destinations
    const buildUnifiedColumns = (destinations) => {
      const cols = ['Cliente', 'Destino', 'Producto', 'Nº Goya'];
      const hasAnmar = destinations.includes('ANMAR');
      const hasGaiker = destinations.includes('GAIKER');
      const hasIvami = destinations.includes('IVAMI');
      const hasNonAnmar = hasGaiker || hasIvami || destinations.includes('REGULATORY');
      if (hasAnmar) { cols.push('Ensayo'); cols.push('Nº Anmar'); }
      if (hasGaiker) cols.push('Nº Presupuesto Gaiker');
      if (hasIvami) cols.push('Nº Proforma Ivami');
      cols.push('Precio');
      if (hasNonAnmar) { cols.push('Coste Goya'); cols.push('Margen Goya'); }
      return cols;
    };

    const buildUnifiedRow = (sp, destinations) => {
      const row = [
        sp.sample.client || '',
        sp.sample.destination || '',
        sp.sample.product || '',
        sp.sample.num_ensayo || '',
      ];
      const hasAnmar = destinations.includes('ANMAR');
      const hasGaiker = destinations.includes('GAIKER');
      const hasIvami = destinations.includes('IVAMI');
      const hasNonAnmar = hasGaiker || hasIvami || destinations.includes('REGULATORY');
      if (hasAnmar) {
        row.push(sp.sample.destination === 'ANMAR' ? (sp.sample.ensayo || '') : '');
        row.push(sp.sample.destination === 'ANMAR' ? (sp.sample.num_anmar || '') : '');
      }
      if (hasGaiker) {
        row.push(sp.sample.destination === 'GAIKER' ? (sp.sample.num_presupuesto_gaiker || '') : '');
      }
      if (hasIvami) {
        row.push(sp.sample.destination === 'IVAMI' ? (sp.sample.num_proforma_ivami || '') : '');
      }
      row.push(formatPrice(sp.payment.amount) ? `${formatPrice(sp.payment.amount)} €` : '');
      if (hasNonAnmar) {
        if (sp.sample.destination === 'ANMAR' || sp.sample.destination === 'ESTABILIDADES') {
          row.push('');
          row.push('');
        } else {
          const coste = getCosteGoya(sp);
          row.push(coste ? `${formatPrice(coste)} €` : '—');
          const margen = getMargenGoya(sp);
          row.push(margen != null ? `${formatPrice(margen)} €` : '');
        }
      }
      return row;
    };

    const buildSingleColumns = (destination) => {
      const cols = ['Cliente', 'Producto', 'Nº Goya'];
      if (destination === 'ANMAR') {
        cols.push('Ensayo', 'Nº Anmar');
      } else if (destination === 'GAIKER') {
        cols.push('Nº Presupuesto Gaiker');
      } else if (destination === 'IVAMI') {
        cols.push('Nº Proforma Ivami');
      }
      cols.push('Precio');
      if (destination !== 'ANMAR' && destination !== 'ESTABILIDADES') {
        cols.push('Coste Goya', 'Margen Goya');
      }
      return cols;
    };

    const buildSingleRow = (sp, destination) => {
      const row = [
        sp.sample.client || '',
        sp.sample.product || '',
        sp.sample.num_ensayo || '',
      ];
      if (destination === 'ANMAR') {
        row.push(sp.sample.ensayo || '');
        row.push(sp.sample.num_anmar || '');
      } else if (destination === 'GAIKER') {
        row.push(sp.sample.num_presupuesto_gaiker || '');
      } else if (destination === 'IVAMI') {
        row.push(sp.sample.num_proforma_ivami || '');
      }
      row.push(formatPrice(sp.payment.amount) ? `${formatPrice(sp.payment.amount)} €` : '');
      if (destination !== 'ANMAR' && destination !== 'ESTABILIDADES') {
        const coste = getCosteGoya(sp);
        row.push(coste ? `${formatPrice(coste)} €` : '—');
        const margen = getMargenGoya(sp);
        row.push(margen != null ? `${formatPrice(margen)} €` : '');
      }
      return row;
    };

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Informe de Facturación', pageWidth / 2, 15, { align: 'center' });

    // Subtitle with filter info
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const filterParts = [];
    if (activeDestination !== 'all') filterParts.push(`Destino: ${activeDestination}`);
    if (filters.year !== 'all') filterParts.push(`Año: ${filters.year}`);
    if (filters.month !== 'all') {
      const monthLabel = MONTHS.find(m => m.value === filters.month)?.label || filters.month;
      filterParts.push(`Mes: ${monthLabel}`);
    }
    if (filters.billing_status !== 'all') filterParts.push(`Estado: ${filters.billing_status}`);
    const filterText = filterParts.length > 0 ? filterParts.join(' | ') : 'Sin filtros aplicados';
    doc.text(filterText, pageWidth / 2, 21, { align: 'center' });

    let startY = 27;

    if (activeDestination !== 'all') {
      // Single destination: one table without "Destino" column
      const columns = buildSingleColumns(activeDestination);
      const rows = filteredSamples.map(sp => buildSingleRow(sp, activeDestination));

      autoTable(doc, {
        startY,
        head: [columns],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: { [columns.length - 1]: { halign: 'right' } },
      });
    } else {
      // All destinations: single unified table sorted by client
      const columns = buildUnifiedColumns(destinationsInData);
      const sortedSamples = [...filteredSamples].sort((a, b) => 
        (a.sample.client || '').toLowerCase().localeCompare((b.sample.client || '').toLowerCase(), 'es')
      );
      const rows = sortedSamples.map(sp => buildUnifiedRow(sp, destinationsInData));

      autoTable(doc, {
        startY,
        head: [columns],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: { [columns.length - 1]: { halign: 'right' } },
      });
    }

    // Footer: totals
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : startY + 8;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const showMargins = activeDestination !== 'ANMAR' && activeDestination !== 'ESTABILIDADES' &&
      (activeDestination !== 'all' || destinationsInData.some(d => d !== 'ANMAR' && d !== 'ESTABILIDADES'));
    if (showMargins) {
      doc.text(`Total Importe: ${formatPrice(getTotalAmount())} €  |  Coste Goya: ${formatPrice(getTotalCosteGoya())} €  |  Margen Goya: ${formatPrice(getTotalMargenGoya())} €`, pageWidth - 14, finalY, { align: 'right' });
    } else {
      doc.text(`Total: ${formatPrice(getTotalAmount())} €`, pageWidth - 14, finalY, { align: 'right' });
    }

    // Date generated
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`, 14, doc.internal.pageSize.getHeight() - 8);

    // Save
    const fileName = `facturacion_${activeDestination !== 'all' ? activeDestination.toLowerCase() : 'todos'}_${filters.year !== 'all' ? filters.year : 'todos'}.pdf`;
    doc.save(fileName);
    toast.success('PDF generado correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">Cargando datos de facturación...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Facturación</h1>
        <p className="text-gray-600 mt-1">
          Gestión y seguimiento de hitos de facturación
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter size={20} />
              <span>Filtros</span>
            </div>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpiar Filtros
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Estado de Facturación</Label>
              <Select
                value={filters.billing_status}
                onValueChange={(value) => setFilters({ ...filters, billing_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="Pte facturar">Pte facturar</SelectItem>
                  <SelectItem value="Facturado">Facturado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Año</Label>
              <Select
                value={filters.year}
                onValueChange={(value) => setFilters({ ...filters, year: value, month: 'all' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los años</SelectItem>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Mes</Label>
              <Select
                value={filters.month}
                onValueChange={(value) => setFilters({ ...filters, month: value })}
                disabled={filters.year === 'all'}
              >
                <SelectTrigger className={filters.year === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}>
                  <SelectValue placeholder={filters.year === 'all' ? 'Selecciona año primero' : 'Todos los meses'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los meses</SelectItem>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isRestrictedUser && (
              <div className="space-y-2">
                <Label>Destino</Label>
                <Select
                  value={filters.destination}
                  onValueChange={(value) => setFilters({ ...filters, destination: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los destinos</SelectItem>
                    <SelectItem value="ANMAR">ANMAR</SelectItem>
                    <SelectItem value="GAIKER">GAIKER</SelectItem>
                    <SelectItem value="IVAMI">IVAMI</SelectItem>
                    <SelectItem value="REGULATORY">REGULATORY</SelectItem>
                    <SelectItem value="ESTABILIDADES">ESTABILIDADES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {isRestrictedUser && (
              <div className="space-y-2">
                <Label>Destino (Acceso Restringido)</Label>
                <Input
                  value={restrictedDestination}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Hitos</p>
              <p className="text-3xl font-bold mt-1">{filteredSamples.length}</p>
            </div>
            <div className="text-center">
              <p className="text-blue-100 text-sm font-medium">Importe Total</p>
              <p className="text-3xl font-bold mt-1">€{formatPrice(getTotalAmount())}</p>
            </div>
            {activeDestination !== 'ANMAR' && activeDestination !== 'ESTABILIDADES' && (
              <>
                <div className="text-center">
                  <p className="text-blue-100 text-sm font-medium">Coste Goya Total</p>
                  <p className="text-3xl font-bold mt-1">€{formatPrice(getTotalCosteGoya())}</p>
                </div>
                <div className="text-center">
                  <p className="text-blue-100 text-sm font-medium">Margen Goya Total</p>
                  <p className="text-3xl font-bold mt-1">€{formatPrice(getTotalMargenGoya())}</p>
                </div>
              </>
            )}
            <Button
              variant="outline"
              onClick={generatePDF}
              disabled={filteredSamples.length === 0}
              className="bg-white text-blue-600 hover:bg-blue-50 border-white font-semibold"
              data-testid="generate-pdf-btn"
            >
              <Download size={16} className="mr-2" />
              Generar PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} />
            <span>Resultados ({filteredSamples.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSamples.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No se encontraron hitos con los filtros aplicados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSamples.map((sp, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-lg text-gray-900">
                          {sp.sample.client}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sp.sample.destination === 'ANMAR' ? 'bg-green-100 text-green-800' :
                          sp.sample.destination === 'GAIKER' ? 'bg-blue-100 text-blue-800' :
                          sp.sample.destination === 'IVAMI' ? 'bg-purple-100 text-purple-800' :
                          sp.sample.destination === 'ESTABILIDADES' ? 'bg-teal-100 text-teal-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {sp.sample.destination}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Producto:</span>
                          <p className="font-medium">{sp.sample.product || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Nº Goya:</span>
                          <p className="font-medium">{sp.sample.num_ensayo || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Hito:</span>
                          <p className="font-medium">{formatPaymentName(sp.payment.payment_number, getTotalPaymentsFromType(sp.sample.payment_config?.payment_type))}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Mes/Año:</span>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar size={14} />
                            {sp.payment.payment_month ? 
                              MONTHS.find(m => m.value === sp.payment.payment_month)?.label : 
                              'Sin mes'}
                            {' / '}
                            {sp.payment.payment_year || 'Sin año'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 block mb-1">Estado:</span>
                          <Select
                            value={sp.payment.billing_status || 'Pte facturar'}
                            onValueChange={(value) => 
                              handleBillingStatusChange(
                                sp.sample.id, 
                                sp.payment.payment_number - 1, 
                                value
                              )
                            }
                          >
                            <SelectTrigger className="w-36 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pte facturar">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                  Pte facturar
                                </span>
                              </SelectItem>
                              <SelectItem value="Facturado">
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  Facturado
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right ml-4 space-y-2">
                      <div>
                        <p className="text-xs text-gray-500">Importe</p>
                        <p className="text-xl font-bold text-green-600">
                          €{formatPrice(sp.payment.amount)}
                        </p>
                      </div>
                      {sp.sample.destination !== 'ANMAR' && sp.sample.destination !== 'ESTABILIDADES' && (
                        <>
                          <div>
                            <p className="text-xs text-gray-500">Coste Goya</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {getCosteGoya(sp) ? `€${formatPrice(getCosteGoya(sp))}` : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Margen Goya</p>
                            <p className="text-sm font-semibold text-blue-600">
                              €{formatPrice(getMargenGoya(sp))}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Facturacion;
